import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const invoiceTemplateApi = createApi({
  reducerPath: "invoiceTemplateApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["InvoiceTemplate"],
  endpoints: (builder) => ({
    /* -------------------------
       GET: all predefined templates
       ------------------------- */
    getInvoiceTemplates: builder.query({
      query: () => ({
        url: "/invoice-template-design/invoice-templates",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    /* -------------------------
       GET: tenant selected template
       ------------------------- */
    getTenantInvoiceTemplate: builder.query({
      query: () => ({
        url: "/invoice-template-design/tenant/invoice-template",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    /* -------------------------
       SAVE / UPDATE tenant template
       ------------------------- */
    saveTenantInvoiceTemplate: builder.mutation({
      query: (invoiceTemplateKey) => ({
        url: "/invoice-template-design/tenant/invoice-template",
        method: "PUT",
        body: { invoiceTemplateKey },
      }),
      invalidatesTags: ["InvoiceTemplate"],
    }),

    uploadInvoiceTemplate: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("template", file);

        return {
          url: "/templates/upload",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["InvoiceTemplate"],
    }),

    getAdminInvoiceTemplates: builder.query({
      query: () => ({
        url: "/templates",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    getTenantUrdTemplate: builder.query({
      query: () => ({
        url: "/invoice-template-design/tenant/urd-template",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    saveTenantUrdTemplate: builder.mutation({
      query: (urdTemplateKey) => ({
        url: "/invoice-template-design/tenant/urd-template",
        method: "PUT",
        body: { urdTemplateKey },
      }),
      invalidatesTags: ["InvoiceTemplate"],
    }),

    uploadEstimateTemplate: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("template", file);

        return {
          url: "/templates/estimate/upload",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["InvoiceTemplate"],
    }),

    getAdminEstimateTemplates: builder.query({
      query: () => ({
        url: "/templates/estimate",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    getEstimateTemplates: builder.query({
      query: () => ({
        url: "/invoice-template-design/estimate-templates",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    getTenantEstimateTemplate: builder.query({
      query: () => ({
        url: "/invoice-template-design/tenant/estimate-template",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    saveTenantEstimateTemplate: builder.mutation({
      query: (estimateTemplateKey) => ({
        url: "/invoice-template-design/tenant/estimate-template",
        method: "PUT",
        body: { estimateTemplateKey },
      }),
      invalidatesTags: ["InvoiceTemplate"],
    }),

    getQuotationTemplates: builder.query({
      query: () => ({
        url: "/invoice-template-design/quotation-templates",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    getTenantQuotationTemplate: builder.query({
      query: () => ({
        url: "/invoice-template-design/tenant/quotation-template",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    saveTenantQuotationTemplate: builder.mutation({
      query: (quotationTemplateKey) => ({
        url: "/invoice-template-design/tenant/quotation-template",
        method: "PUT",
        body: { quotationTemplateKey },
      }),
      invalidatesTags: ["InvoiceTemplate"],
    }),

    uploadQuotationTemplate: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("template", file);

        return {
          url: "/templates/quotation/upload",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["InvoiceTemplate"],
    }),

    getAdminQuotationTemplates: builder.query({
      query: () => ({
        url: "/templates/quotation",
        method: "GET",
      }),
      providesTags: ["InvoiceTemplate"],
    }),

    uploadUrdTemplate: builder.mutation({
  query: (file) => {
    const formData = new FormData();
    formData.append("template", file);

    return {
      url: "/templates/urd/upload",
      method: "POST",
      body: formData,
    };
  },
  invalidatesTags: ["InvoiceTemplate"],
}),

getAdminUrdTemplates: builder.query({
  query: () => ({
    url: "/templates/urd",
    method: "GET",
  }),
  providesTags: ["InvoiceTemplate"],
}),

getUrdTemplates: builder.query({
  query: () => ({
    url: "/invoice-template-design/urd-templates",
    method: "GET",
  }),
  providesTags: ["InvoiceTemplate"],
}),

getReceiptTemplates: builder.query({
  query: () => ({
    url: "/invoice-template-design/receipt-templates",
    method: "GET",
  }),
  providesTags: ["InvoiceTemplate"],
}),

getTenantReceiptTemplate: builder.query({
  query: () => ({
    url: "/invoice-template-design/tenant/receipt-template",
    method: "GET",
  }),
  providesTags: ["InvoiceTemplate"],
}),

saveTenantReceiptTemplate: builder.mutation({
  query: (receiptTemplateKey) => ({
    url: "/invoice-template-design/tenant/receipt-template",
    method: "PUT",
    body: { receiptTemplateKey },
  }),
  invalidatesTags: ["InvoiceTemplate"],
}),

uploadReceiptTemplate: builder.mutation({
  query: (file) => {
    const formData = new FormData();
    formData.append("template", file);

    return {
      url: "/templates/receipt/upload",
      method: "POST",
      body: formData,
    };
  },
  invalidatesTags: ["InvoiceTemplate"],
}),

getAdminReceiptTemplates: builder.query({
  query: () => ({
    url: "/templates/receipt",
    method: "GET",
  }),
  providesTags: ["InvoiceTemplate"],
}),


previewTemplate: builder.mutation({
  query: ({ type, key, data }) => ({
    url: `/invoice-template-design/template-preview/${type}/${key}`,
    method: "POST",
    body: data,
    responseHandler: (response) => response.text(),
  }),
}),


  }),
});

export const {
  useGetInvoiceTemplatesQuery,
  useGetTenantInvoiceTemplateQuery,
  useLazyGetTenantInvoiceTemplateQuery,
  useSaveTenantInvoiceTemplateMutation,
  useUploadInvoiceTemplateMutation,
  useGetAdminInvoiceTemplatesQuery,
  useGetTenantUrdTemplateQuery,
  useSaveTenantUrdTemplateMutation,
  useUploadEstimateTemplateMutation,
  useGetAdminEstimateTemplatesQuery,
  useGetEstimateTemplatesQuery,
  useGetTenantEstimateTemplateQuery,
  useSaveTenantEstimateTemplateMutation,
  useGetQuotationTemplatesQuery,
  useGetTenantQuotationTemplateQuery,
  useSaveTenantQuotationTemplateMutation,
  useUploadQuotationTemplateMutation,
  useGetAdminQuotationTemplatesQuery,
  useUploadUrdTemplateMutation,
useGetAdminUrdTemplatesQuery,
useGetUrdTemplatesQuery,
useGetReceiptTemplatesQuery,
useGetTenantReceiptTemplateQuery,
useSaveTenantReceiptTemplateMutation,
useUploadReceiptTemplateMutation,
useGetAdminReceiptTemplatesQuery,
usePreviewTemplateMutation,
} = invoiceTemplateApi;

export default invoiceTemplateApi;
